/**
 * Finding a seller in a tenant's full list. Pure and DOM-free, and in its own
 * module because two stations need it: Horse Check-In (a late consignment must
 * name its seller) and the ring console's add-lot dialog. Each is its own
 * bundle with its own boot code, so neither can import the other.
 */

// A local, narrow view of Consignor (same convention as CheckinLot in
// horse-checkin.ts) - the stations only ever need enough to search for someone
// and show their name back; the tax/office fields belong to the settlement side.
export interface Seller {
  id: string;
  name: string;
  sellerNumber: number | null;
  phone: string | null;
}

/**
 * Filter-as-you-type over a tenant's full seller list (brief: "there may be
 * hundreds" - a plain <select> was never in scope). Mirrors findBidders's
 * digit-aware matching: a query that IS a bare number is read as a
 * seller number first (exact, wins outright over any name that happens to
 * contain the same digits), otherwise it's a name substring or - at 4+
 * digits, same floor as phoneTail - a phone fragment. Capped, because
 * "hundreds" rendered as one long list defeats the point of searching.
 */
export function searchSellers<T extends { name: string; phone: string | null; sellerNumber: number | null }>(
  sellers: readonly T[],
  query: string,
  limit = 8,
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const digits = q.replace(/\D/g, '');
  if (digits && digits === q) {
    const exact = sellers.filter((s) => s.sellerNumber === Number(digits));
    if (exact.length) return exact.slice(0, limit);
  }
  return sellers
    .filter((s) => s.name.toLowerCase().includes(q) || (digits.length >= 4 && !!s.phone && s.phone.replace(/\D/g, '').includes(digits)))
    .slice(0, limit);
}
